import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ExternalLink, GitBranch, PackagePlus, Plus, Rocket, Search } from "lucide-react";

import { PageHeader } from "../components/PageHeader.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { StatusIcon } from "../components/StatusBadge.jsx";
import { Elapsed, EmptyState, Modal, Spinner, ago, duration, useToast } from "../components/ui.jsx";
import { RepoPicker } from "./Workspaces.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/**
 * Every project, searchable.
 *
 * The Overview shows what is happening; this shows what exists. A grid of fifteen
 * cards is fine to look at and a poor way to find one, which is why the search is
 * the page's primary control rather than an afterthought.
 */
export function Projects() {
    const [projects, setProjects] = useState(null);
    const [{ q: query }, setUrl] = useUrlStates({ q: "" });
    const setQuery = (value) => setUrl("q", value);
    const [busy, setBusy] = useState(null);
    const [starting, setStarting] = useState(false);
    const toast = useToast();

    const load = () => api.projects.list().then(setProjects).catch(() => setProjects([]));
    // A card shows its project's last deployment, so a deploy moving redraws it too.
    useLive(["projects", "deployments"], load);

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return projects ?? [];
        return (projects ?? []).filter((project) =>
            `${project.slug} ${project.name ?? ""} ${project.gitlab.path} ${project.branch}`.toLowerCase().includes(needle)
        );
    }, [projects, query]);

    if (!projects) return <Spinner className="muted" />;

    const deploy = async (slug) => {
        setBusy(slug);
        try {
            const result = await api.projects.deploy(slug);
            toast(`${slug} #${result.number} started`);
            load();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            <PageHeader
                title="Projects"
                meta={query ? `${shown.length} of ${projects.length}` : `${projects.length} onboarded`}
                actions={
                    <>
                        {projects.length > 0 ? (
                            <div className="relative w-full sm:w-56">
                                <Search size={14} className="muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
                                <input
                                    className="input pl-8"
                                    placeholder="Search projects"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    aria-label="Search projects"
                                />
                            </div>
                        ) : null}
                        <button className="btn-primary px-3 py-1.5" onClick={() => setStarting(true)}>
                            <Plus size={14} /> New project
                        </button>
                    </>
                }
            />

            {shown.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={PackagePlus}
                        title={projects.length === 0 ? "No projects yet" : "Nothing matches"}
                        hint={
                            projects.length === 0
                                ? "Start one from a repository and it begins as a working copy in ~/dev; onboard one that is already running with the scaling-ops skill."
                                : "Try a different search."
                        }
                    />
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {shown.map((project) => (
                        <ProjectCard key={project.slug} project={project} busy={busy === project.slug} onDeploy={() => deploy(project.slug)} />
                    ))}
                </div>
            )}

            <NewProject open={starting} onClose={() => setStarting(false)} onDone={load} />
        </>
    );
}

/**
 * Start a project: a repository, a working copy, a draft.
 *
 * A project begins here rather than at its first deploy. What it gets is a
 * checkout in `~/dev` a session can work in and a draft manifest to fill in; the
 * domain, the `/data` root and the first deploy come later, when there is
 * something to deploy.
 */
function NewProject({ open, onClose, onDone }) {
    const [repo, setRepo] = useState(null);
    const [fresh, setFresh] = useState(false);
    const [slug, setSlug] = useState("");
    const [picking, setPicking] = useState(false);
    const [session, setSession] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        if (!open) return;
        setRepo(null);
        setFresh(false);
        setSlug("");
        setSession(false);
        setError(null);
    }, [open]);

    const ready = slug.trim() && (fresh || repo);

    const submit = async () => {
        setBusy(true);
        setError(null);
        const name = slug.trim();
        try {
            const result = await api.projects.start({
                slug: name,
                repo: fresh ? null : { id: repo.id, path: repo.path, ssh: repo.ssh, branch: repo.branch },
                createRepo: fresh ? { name } : null,
                startSession: session
            });
            onClose();
            await onDone();
            // Each part fails on its own, so each is reported: a draft with no
            // working copy is still a draft worth keeping.
            if (result.workspaceError) toast(`${name}: ${result.workspaceError}`, "error");
            else if (result.sessionError) toast(`${name}: ${result.sessionError}`, "error");
            else toast(`${name} started`);
            navigate(`/projects/${name}`);
        } catch (problem) {
            setError(problem.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Modal open={open && !picking} onClose={onClose} title="New project">
                <div className="space-y-4">
                    <div>
                        <span className="label">Repository</span>
                        <div className="mt-1 flex gap-2">
                            <button className="btn-ghost min-w-0 flex-1 justify-between px-3 py-2" onClick={() => { setFresh(false); setPicking(true); }}>
                                <span className="truncate">{!fresh && repo ? repo.path : "Choose a repository"}</span>
                                <GitBranch size={13} className="shrink-0" aria-hidden />
                            </button>
                            <button
                                className={fresh ? "btn-primary px-3 py-2" : "btn-ghost px-3 py-2"}
                                onClick={() => { setFresh(!fresh); setRepo(null); }}
                                title="Create a new private repository in GitLab"
                            >
                                <Plus size={13} /> New
                            </button>
                        </div>
                        {fresh ? (
                            <span className="muted mt-1 block text-xs">
                                A new private repository called <code className="font-mono">{slug || "<slug>"}</code>, with a first commit so it has a
                                <code className="font-mono"> main</code> to clone.
                            </span>
                        ) : null}
                    </div>

                    <label className="block">
                        <span className="label">Slug</span>
                        <input className="input mt-1" value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="qto" />
                        <span className="muted mt-1 block text-xs">
                            The project name everywhere: <code className="font-mono">~/dev/{slug || "<slug>"}</code> to work in,
                            <code className="font-mono"> /data/{slug || "<slug>"}</code> when it is deployed.
                        </span>
                    </label>

                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="size-4" checked={session} onChange={(event) => setSession(event.target.checked)} />
                        Start a Claude session on it
                    </label>

                    {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}

                    <div className="flex justify-end gap-2">
                        <button className="btn-ghost px-3 py-1.5" onClick={onClose}>Cancel</button>
                        <button className="btn-primary px-3 py-1.5" disabled={!ready || busy} onClick={submit}>
                            {busy ? <Spinner size={13} /> : <Plus size={14} />} {busy ? "Starting…" : "Start project"}
                        </button>
                    </div>
                </div>
            </Modal>

            <RepoPicker
                open={open && picking}
                onClose={() => setPicking(false)}
                onPick={(chosen) => {
                    setRepo(chosen);
                    if (!slug) setSlug(chosen.path.split("/").pop().replace(/\.git$/, "").toLowerCase());
                }}
            />
        </>
    );
}

export function ProjectCard({ project, busy, onDeploy }) {
    const last = project.lastDeployment;
    // The first domain is the canonical one. It is what you actually want to click,
    // and until now the card did not show it at all.
    const domain = project.domains?.[0]?.replace(/^https?:\/\//, "") ?? null;

    return (
        <div className="card group flex min-w-0 flex-col gap-3.5 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-700">
            <div className="flex items-start gap-3">
                <ProjectIcon slug={project.slug} hash={project.favicon?.hash} size={40} framed />

                <div className="min-w-0 flex-1">
                    <Link to={`/projects/${project.slug}`} className="flex items-center gap-1.5 text-[15px] font-semibold leading-tight text-gray-900 hover:underline dark:text-gray-100">
                        <span className="truncate">{project.slug}</span>
                    </Link>
                    {domain ? (
                        <a
                            href={`https://${domain}`}
                            target="_blank"
                            rel="noreferrer"
                            className="muted mt-0.5 flex items-center gap-1 text-xs hover:text-gray-900 dark:hover:text-gray-100"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <span className="truncate">{domain}</span>
                            <ExternalLink size={10} className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                        </a>
                    ) : (
                        <p className="muted mt-0.5 truncate text-xs">no domain set</p>
                    )}
                </div>

                {project.state === "draft" || project.paused ? (
                    <div className="flex shrink-0 items-center gap-2">
                        {project.state === "draft" ? <span className="badge-queued">draft</span> : null}
                        {project.paused ? <span className="badge-warn">paused</span> : null}
                    </div>
                ) : null}
            </div>

            {last ? (
                <div className="min-w-0 space-y-1 text-xs">
                    {/* The same line a phone row reads: the glyph, a running one
                        counting beside it, what shipped, and when — no pill. */}
                    <p className="flex min-w-0 items-center gap-2">
                        <StatusIcon status={last.status} />
                        {last.status === "running" ? <Elapsed since={last.startedAt} className="shrink-0 text-blue-600 dark:text-blue-400" /> : null}
                        <span className="muted truncate tabular-nums">
                            #{last.number} · {last.actor}{last.durationMs ? ` · ${duration(last.durationMs)}` : ""}
                        </span>
                        <span className="muted ml-auto shrink-0">{ago(last.startedAt)}</span>
                    </p>
                    {/* A deploy triggered by hand has no commit; saying so in the same
                        slot as a message reads as an error rather than as a fact. */}
                    {last.commit?.shortSha ? (
                        <p className="flex min-w-0 items-baseline gap-1.5" title={last.commit.message ?? ""}>
                            <code className="shrink-0 font-mono text-gray-700 dark:text-gray-300">{last.commit.shortSha}</code>
                            <span className="muted truncate">{last.commit.message}</span>
                        </p>
                    ) : (
                        <p className="muted truncate">{last.trigger === "webhook" ? "pushed" : `${last.trigger ?? "manual"} deploy`}</p>
                    )}
                </div>
            ) : (
                <p className="muted text-xs">{project.state === "draft" ? "a working copy and a manifest being written" : "never deployed"}</p>
            )}

            <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
                <p className="muted flex min-w-0 items-center gap-1 text-xs">
                    <GitBranch size={11} className="shrink-0" aria-hidden />
                    <span className="truncate">{project.gitlab.path}</span>
                    <span className="shrink-0">· {project.branch}</span>
                </p>
                <div className="flex shrink-0 gap-1.5">
                    <button
                        className="btn-ghost px-2.5 py-1"
                        onClick={onDeploy}
                        disabled={busy || project.paused || project.state === "draft"}
                        title={project.state === "draft" ? "Still a draft: finish the manifest and activate it" : "Deploy now"}
                    >
                        <Rocket size={12} /> Deploy
                    </button>
                    <Link to={`/projects/${project.slug}`} className="btn-ghost px-2.5 py-1">Open</Link>
                </div>
            </div>
        </div>
    );
}
